<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-shipment-overview">
    <nx-report-header title="汽运发运总览看板">
      <div class="kb-header-actions">
        <el-radio-group v-model="periodType" size="small" @change="onPeriodTypeChange">
          <el-radio-button label="year">年</el-radio-button>
          <el-radio-button label="month">月</el-radio-button>
          <el-radio-button label="day">日</el-radio-button>
        </el-radio-group>
        <el-date-picker
          :key="datePickerType"
          v-model="queryDate"
          :type="datePickerType"
          :value-format="dateValueFormat"
          :placeholder="datePlaceholder"
          size="small"
          :style="{ width: datePickerWidth }"
          @change="loadData"
        />
        <el-button size="small" icon="el-icon-refresh" @click="loadData">刷新数据</el-button>
      </div>
    </nx-report-header>

    <div class="kb-kpi-row cols-4">
      <div v-for="(k, i) in kpis" :key="i" class="kb-kpi-card">
        <div class="kb-kpi-label">{{ k.label }}</div>
        <div class="kb-kpi-value">{{ k.value }}</div>
        <div class="kb-kpi-sub" v-html="k.sub" />
      </div>
    </div>

    <div class="kb-charts-row">
      <div class="kb-chart-card">
        <div class="kb-chart-head">
          <span>煤种发运对比</span>
        </div>
        <div ref="materialChart" class="kb-chart-box" />
      </div>
      <div class="kb-chart-card">
        <div class="kb-chart-head">
          <span>{{ trendChartTitle }}</span>
        </div>
        <div ref="trendChart" class="kb-chart-box" />
      </div>
    </div>

    <div class="kb-section">
      <div class="kb-section-head">
        <span>发运监控明细</span>
      </div>
      <el-table :data="shipments" border stripe size="small" style="width: 100%">
        <el-table-column prop="shipper" label="货主" min-width="120" />
        <el-table-column prop="plate" label="车牌号" width="120" />
        <el-table-column prop="coalType" label="煤种" width="120" />
        <el-table-column prop="netWeight" label="净重(吨)" width="120" align="right">
          <template slot-scope="{ row }">{{ row.netWeight != null && row.netWeight !== '' ? row.netWeight : '--' }}</template>
        </el-table-column>
        <el-table-column prop="entryTime" label="进门时间" width="120" />
        <el-table-column prop="exitTime" label="出门时间" width="120">
          <template slot-scope="{ row }">{{ row.exitTime || '--' }}</template>
        </el-table-column>
        <el-table-column prop="duration" label="作业时长" width="100" />
        <el-table-column label="状态" width="88">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 'completed' ? 'success' : 'primary'" size="mini">
              {{ row.status === 'completed' ? '已完成' : '进行中' }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchShipmentOverview } from './api'

export default {
  name: 'KanbanShipmentOverview',
  components: { NxReportHeader },
  data() {
    return {
      loading: false,
      periodType: 'day',
      queryDate: null,
      coalTypes: [],
      shipments: [],
      hourlyTrend: { hours: [], values: [] },
      kpis: [],
      chartInstances: [],
      lastOverview: null
    }
  },
  computed: {
    datePickerType() {
      return this.periodType === 'year' ? 'year' : this.periodType === 'month' ? 'month' : 'date'
    },
    dateValueFormat() {
      return this.periodType === 'year' ? 'yyyy' : this.periodType === 'month' ? 'yyyy-MM' : 'yyyy-MM-dd'
    },
    datePlaceholder() {
      return this.periodType === 'year' ? '选择年份' : this.periodType === 'month' ? '选择月份' : '选择日期'
    },
    datePickerWidth() {
      return this.periodType === 'year' ? '110px' : this.periodType === 'month' ? '130px' : '150px'
    },
    periodLabel() {
      return this.periodType === 'year' ? '年度' : this.periodType === 'month' ? '月度' : '当日'
    },
    trendChartTitle() {
      return this.periodType === 'year' ? '发运趋势（月）' : this.periodType === 'month' ? '发运趋势（日）' : '发运趋势（小时）'
    }
  },
  mounted() {
    this.queryDate = this.formatPeriodValue(this.periodType, new Date())
    this.loadData()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    this.disposeCharts()
  },
  methods: {
    formatDateStr(d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    formatMonthStr(d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      return `${y}-${m}`
    },
    formatYearStr(d) {
      return String(d.getFullYear())
    },
    formatPeriodValue(type, d) {
      if (type === 'year') return this.formatYearStr(d)
      if (type === 'month') return this.formatMonthStr(d)
      return this.formatDateStr(d)
    },
    onPeriodTypeChange() {
      this.queryDate = this.formatPeriodValue(this.periodType, new Date())
      this.loadData()
    },
    async loadData() {
      this.loading = true
      try {
        const params = { periodType: this.periodType }
        if (this.queryDate) params.queryDate = this.queryDate
        const data = await fetchShipmentOverview(params)
        this.lastOverview = data
        this.coalTypes = data.coalTypes || []
        this.shipments = data.shipments || []
        this.hourlyTrend = data.hourlyTrend || { hours: [], values: [] }
        this.buildKpis()
        this.$nextTick(() => this.renderCharts())
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    buildKpis() {
      const types = this.coalTypes
      const totalPlan = types.reduce((s, c) => s + (Number(c.plan) || 0), 0)
      const totalActual = types.reduce((s, c) => s + (Number(c.actual) || 0), 0)
      const rate = totalPlan ? ((totalActual / totalPlan) * 100).toFixed(1) : '0'
      const remaining = Math.max(0, totalPlan - totalActual)
      const done =
        this.lastOverview && this.lastOverview.todayCompletedVehicles != null
          ? this.lastOverview.todayCompletedVehicles
          : this.shipments.filter((r) => r.status === 'completed').length
      const inProg =
        this.lastOverview && this.lastOverview.todayInProgressVehicles != null
          ? this.lastOverview.todayInProgressVehicles
          : this.shipments.filter((r) => r.status !== 'completed').length
      const avgMin = this.lastOverview && this.lastOverview.avgDurationMinutes
      const avgNum = avgMin != null && avgMin !== '' ? Number(avgMin) : NaN
      let tag = 'kb-tag-green'
      let tagText = '正常'
      if (Number(rate) < 70) {
        tag = 'kb-tag-red'
        tagText = '滞后'
      } else if (Number(rate) < 90) {
        tag = 'kb-tag-yellow'
        tagText = '正常'
      }
      if (Number(rate) >= 95) tagText = '超前'
      this.kpis = [
        { label: `${this.periodLabel}计划发运总量`, value: `${totalPlan.toFixed(0)} 吨`, sub: `计划车次约 ${Math.ceil(totalPlan / 35)} 车` },
        {
          label: '实际完成量',
          value: `${totalActual.toFixed(0)} 吨`,
          sub: `已完成 <strong>${done}</strong> 车 · 在途 <strong>${inProg}</strong> 车`
        },
        {
          label: '总体完成率',
          value: `${rate}%`,
          sub: `<span class="kb-tag ${tag}">${tagText}</span> 剩余 ${remaining.toFixed(0)} 吨`
        },
        {
          label: '平均作业时长',
          value: !Number.isNaN(avgNum) && avgNum > 0 ? `${avgNum.toFixed(1)} 分钟` : '--',
          sub: !Number.isNaN(avgNum) && avgNum > 0 ? '已完成车辆均值' : '暂无已完成车辆时长样本'
        }
      ]
    },
    disposeCharts() {
      this.chartInstances.forEach((c) => c && c.dispose())
      this.chartInstances = []
    },
    onResize() {
      this.chartInstances.forEach((c) => c && c.resize())
    },
    renderCharts() {
      this.disposeCharts()
      const m = echarts.init(this.$refs.materialChart)
      const t = echarts.init(this.$refs.trendChart)
      this.chartInstances = [m, t]
      const planData = this.coalTypes.map((c) => Number(c.plan) || 0)
      const actualData = this.coalTypes.map((c) => Number(c.actual) || 0)
      m.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { data: ['计划(吨)', '实际(吨)'], textStyle: { color: '#334155' } },
        xAxis: { type: 'category', data: this.coalTypes.map((c) => c.name), axisLabel: { color: '#475569' } },
        yAxis: { type: 'value', name: '吨' },
        series: [
          { name: '计划(吨)', type: 'bar', data: planData, itemStyle: { color: '#94a3b8', borderRadius: [6, 6, 0, 0] } },
          { name: '实际(吨)', type: 'bar', data: actualData, itemStyle: { color: '#3b82f6', borderRadius: [6, 6, 0, 0] } }
        ]
      })
      t.setOption({
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: this.hourlyTrend.hours, axisLabel: { rotate: 30, color: '#475569' } },
        yAxis: { type: 'value', name: '发运量(吨)' },
        series: [
          {
            type: 'line',
            data: (this.hourlyTrend.values || []).map((v) => Number(v) || 0),
            smooth: true,
            lineStyle: { color: '#f97316', width: 3 },
            areaStyle: { opacity: 0.1, color: '#f97316' }
          }
        ]
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
